import type { KartState } from '../physics/vehicle';

/** Half of each kart's footprint, roughly — see LEN/WID in render/entities.ts. */
export const KART_RADIUS = 0.85; // m

/** Fraction of closing speed absorbed on contact, rather than bounced back. */
const CONTACT_DAMPING = 0.55;

/**
 * Keeps racing karts from occupying the same space.
 *
 * Not a real rigid-body solver — each overlapping pair is pushed apart along
 * the line between their centres and loses some of the speed at which they
 * were closing. That is enough to make blocking and squeezing past feel
 * physical without touching the single-kart physics the rest of the game
 * depends on: this only ever runs when more than one kart is on track, so
 * solo laps are completely unaffected.
 */
export function resolveKartCollisions(karts: KartState[], radius = KART_RADIUS): void {
  const minDist = radius * 2;
  for (let i = 0; i < karts.length; i++) {
    for (let j = i + 1; j < karts.length; j++) {
      const a = karts[i];
      const b = karts[j];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.hypot(dx, dy);
      if (dist >= minDist || dist < 1e-6) continue;

      const nx = dx / dist;
      const ny = dy / dist;

      // Separate so neither kart is left inside the other.
      const overlap = minDist - dist;
      a.x -= nx * overlap * 0.5;
      a.y -= ny * overlap * 0.5;
      b.x += nx * overlap * 0.5;
      b.y += ny * overlap * 0.5;

      // Bleed off the closing component of relative velocity only — a graze
      // along near-parallel lines should not cost either driver much, but
      // driving straight into the back of someone should.
      const relVx = b.vx - a.vx;
      const relVy = b.vy - a.vy;
      const closing = relVx * nx + relVy * ny;
      if (closing < 0) {
        const impulse = closing * CONTACT_DAMPING * 0.5;
        a.vx += nx * impulse;
        a.vy += ny * impulse;
        b.vx -= nx * impulse;
        b.vy -= ny * impulse;
      }
    }
  }
}
